import { useEffect, useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { extent as d3Extent } from 'd3-array'
import type { LaborSeries, LaborStoreName } from '@/services/laborAnalytics'
import { formatAxisUsd, formatUsd, niceAxisScale } from '@/components/gcp/gcpHelpers'
import {
  bucketTopNOther,
  colorForSeriesKey,
  formatLaborDateLabel,
  getSeriesDates,
  nextHiddenSeriesKeys,
  parseLaborValue,
  pickLaborXLabelIndexes,
  formatLaborPctTooltipCells,
  withTotalSeriesLast,
  type LaborPctTooltipFormatters,
} from '@/components/payroll/laborChartSeries'
import {
  fitLaborTooltipInContainer,
  invertLaborPlotY,
  laborPlotYScale,
  LABOR_TOOLTIP_OFFSET,
  LABOR_TRACKING_LINE_DASH,
  nearestSeriesKeyAtDate,
  peerTooltipPosition,
} from '@/components/payroll/laborChartHover'
import { buildLaborLinePaths } from '@/components/payroll/laborLinePath'

interface HoverState {
  x: number
  y: number
  cursorX: number
  date: string
  dateIndex: number
  key: string
  value: number | null
}

interface LaborLineChartProps {
  series: LaborSeries[]
  ariaLabel: string
  valueFormatter?: (value: number) => string
  yAxisFormatter?: (value: number) => string
  bucketSeries?: boolean
  pctTooltip?: LaborPctTooltipFormatters
  scale?: { max: number; ticks: number[] }
  hideLegend?: boolean
  colorKeys?: string[]
  hiddenKeys?: Set<string>
  onHiddenKeysChange?: (next: Set<string>) => void
  syncHoverDate?: string | null
  onHoverDate?: (date: string | null) => void
  activeKey?: string | null
  onActiveKeyChange?: (key: string | null) => void
  syncHoverYValue?: number | null
  onHoverYValueChange?: (value: number | null) => void
  paneId?: LaborStoreName
  syncSourcePane?: LaborStoreName | null
  onSourcePaneChange?: (pane: LaborStoreName | null) => void
  paneLabel?: string
  closedDates?: ReadonlySet<string>
}

const CONTAINER_HEIGHT = 400
const FALLBACK_WIDTH = 640
const TRACKING_LINE_COLOR = '#9ca3af'
const PCT_TOOLTIP_MIN_WIDTH = 320
const DEFAULT_TOOLTIP_MIN_WIDTH = 192
const TOOLTIP_HEIGHT_FALLBACK = 80
const ISOLATED_POINT_RADIUS = 2.5

function parsePlotValue(
  raw: string | null | undefined,
  date: string,
  closedDates?: ReadonlySet<string>,
): number | null {
  if (closedDates?.has(date)) return null
  if (raw == null || raw === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

export default function LaborLineChart({
  series,
  ariaLabel,
  valueFormatter = formatUsd,
  yAxisFormatter = formatAxisUsd,
  bucketSeries = true,
  pctTooltip,
  scale,
  hideLegend = false,
  colorKeys,
  hiddenKeys: controlledHiddenKeys,
  onHiddenKeysChange,
  syncHoverDate = null,
  onHoverDate,
  activeKey: controlledActiveKey,
  onActiveKeyChange,
  syncHoverYValue = null,
  onHoverYValueChange,
  paneId,
  syncSourcePane = null,
  onSourcePaneChange,
  paneLabel,
  closedDates,
}: LaborLineChartProps) {
  const chartRef = useRef<HTMLDivElement>(null)
  const plotRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [plotSize, setPlotSize] = useState({ width: FALLBACK_WIDTH, height: CONTAINER_HEIGHT })
  const [tooltipSize, setTooltipSize] = useState<{ width: number; height: number } | null>(null)
  const [hover, setHover] = useState<HoverState | null>(null)
  const [internalActiveKey, setInternalActiveKey] = useState<string | null>(null)
  const [internalHiddenKeys, setInternalHiddenKeys] = useState<Set<string>>(() => new Set())
  const hiddenKeys = controlledHiddenKeys ?? internalHiddenKeys
  const setHiddenKeys = onHiddenKeysChange ?? setInternalHiddenKeys
  const isActiveKeyControlled = onActiveKeyChange != null
  const activeKey = isActiveKeyControlled ? (controlledActiveKey ?? null) : internalActiveKey
  const setActiveKey = isActiveKeyControlled ? onActiveKeyChange : setInternalActiveKey

  const chartSeries = useMemo(() => {
    const prepared = bucketSeries ? bucketTopNOther(series) : series
    return withTotalSeriesLast(prepared)
  }, [bucketSeries, series])

  const dates = useMemo(() => getSeriesDates(chartSeries), [chartSeries])

  const linePaths = useMemo(() => {
    return chartSeries.map((entry, index) => ({
      key: entry.key,
      color: colorForSeriesKey(
        entry.key,
        colorKeys ? colorKeys.indexOf(entry.key) : index,
      ),
      values: dates.map((date) => {
        const point = entry.points.find((item) => item.date === date)
        return {
          date,
          value: parsePlotValue(point?.value, date, closedDates),
          numerator: point?.numerator,
          denominator: point?.denominator,
        }
      }),
    }))
  }, [chartSeries, closedDates, colorKeys, dates])

  const showLegend = !hideLegend && linePaths.length > 1
  const visiblePaths = useMemo(
    () => linePaths.filter((line) => !hiddenKeys.has(line.key)),
    [hiddenKeys, linePaths],
  )

  useLayoutEffect(() => {
    const el = plotRef.current
    if (!el) return
    const applySize = () => {
      const width = Math.max(1, el.clientWidth)
      const height = Math.max(1, el.clientHeight)
      setPlotSize((prev) =>
        prev.width === width && prev.height === height ? prev : { width, height },
      )
    }
    applySize()
    const observer = new ResizeObserver(applySize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [dates.length, showLegend])

  const isPeer =
    syncSourcePane != null &&
    paneId != null &&
    paneId !== syncSourcePane &&
    syncHoverDate != null &&
    syncHoverDate !== ''

  useEffect(() => {
    if (isPeer) {
      setHover(null)
    }
  }, [isPeer])

  useLayoutEffect(() => {
    if (dates.length === 0) {
      setTooltipSize(null)
      return
    }
    const hasHover = hover != null
    const hasPeerHover =
      isPeer &&
      syncHoverDate != null &&
      syncHoverDate !== '' &&
      dates.includes(syncHoverDate)
    if (!hasHover && !hasPeerHover) {
      setTooltipSize(null)
      return
    }
    const tooltip = tooltipRef.current
    if (!tooltip) return
    const { offsetWidth, offsetHeight } = tooltip
    setTooltipSize((prev) =>
      prev?.width === offsetWidth && prev?.height === offsetHeight
        ? prev
        : { width: offsetWidth, height: offsetHeight },
    )
  }, [
    dates,
    hover,
    isPeer,
    syncHoverDate,
    pctTooltip,
    visiblePaths.length,
    activeKey,
    hiddenKeys,
  ])

  if (dates.length === 0) {
    return <p className="text-sm text-gray-500">No data for this range.</p>
  }

  const width = Math.max(plotSize.width, 1)
  const height = Math.max(plotSize.height, 1)
  const margin = { top: 16, right: 16, bottom: 32, left: 64 }
  const innerWidth = Math.max(width - margin.left - margin.right, 1)
  const innerHeight = height - margin.top - margin.bottom
  const axisFontSize = 11

  const allValues = visiblePaths.flatMap((line) =>
    line.values
      .map((point) => point.value)
      .filter((value): value is number => value != null),
  )
  const [, maxValue = 0] = d3Extent(allValues) ?? [0, 0]
  const { max: yAxisMax, ticks: yAxisTicks } =
    scale ?? niceAxisScale(Math.max(maxValue, 0), 5)
  const yMin = 0
  const yScale = (value: number) => laborPlotYScale(value, innerHeight, yMin, yAxisMax)
  const xStep = dates.length > 1 ? innerWidth / (dates.length - 1) : 0

  const syncDateIndex =
    syncHoverDate != null && syncHoverDate !== '' ? dates.indexOf(syncHoverDate) : -1
  const trackingDateIndex = hover?.dateIndex ?? (syncDateIndex >= 0 ? syncDateIndex : -1)
  const peerDateIndex = isPeer ? syncDateIndex : -1

  const updateHover = (event: MouseEvent, dateIndex: number) => {
    const chart = chartRef.current
    const plot = plotRef.current
    const date = dates[dateIndex]
    if (!chart || !plot || !date) return
    onHoverDate?.(date)
    onSourcePaneChange?.(paneId ?? null)
    const rect = chart.getBoundingClientRect()
    const plotRect = plot.getBoundingClientRect()
    const cursorX = event.clientX - rect.left
    const yInViewBox =
      plotRect.height > 0
        ? ((event.clientY - plotRect.top) / plotRect.height) * height
        : 0
    const yInner = yInViewBox - margin.top
    const hoverYValue = invertLaborPlotY(yInner, innerHeight, yMin, yAxisMax)
    onHoverYValueChange?.(hoverYValue)
    const nearestKey = nearestSeriesKeyAtDate(visiblePaths, dateIndex, yInner, yScale)
    setActiveKey(nearestKey)
    const primary = visiblePaths.find((line) => line.key === nearestKey)
    setHover({
      x: cursorX + LABOR_TOOLTIP_OFFSET,
      y: event.clientY - rect.top + LABOR_TOOLTIP_OFFSET,
      cursorX,
      date,
      dateIndex,
      key: primary?.key ?? 'total',
      value: primary?.values[dateIndex]?.value ?? null,
    })
  }

  const clearHover = () => {
    onHoverDate?.(null)
    setHover(null)
    if (!isActiveKeyControlled) {
      setActiveKey(null)
    }
  }

  const xLabelIndexes = pickLaborXLabelIndexes(dates.length)

  const highlightYInnerForPeer =
    peerDateIndex >= 0 && activeKey != null
      ? (() => {
          const peerValue = visiblePaths.find((line) => line.key === activeKey)?.values[
            peerDateIndex
          ]?.value
          return peerValue == null ? null : yScale(peerValue)
        })()
      : null

  const peerTooltipPos =
    peerDateIndex >= 0
      ? peerTooltipPosition({
          dateIndex: peerDateIndex,
          xStep,
          marginLeft: margin.left,
          marginTop: margin.top,
          highlightYInner: highlightYInnerForPeer,
        })
      : null

  const tooltipDateIndex = hover?.dateIndex ?? (peerDateIndex >= 0 ? peerDateIndex : -1)
  const showTooltip = hover != null || (isPeer && peerDateIndex >= 0 && peerTooltipPos != null)
  const preferredTooltipLeft = hover?.x ?? peerTooltipPos?.left ?? 0
  const preferredTooltipTop = hover?.y ?? peerTooltipPos?.top ?? 0
  const tooltipCursorX =
    hover?.cursorX ??
    (peerTooltipPos != null ? peerTooltipPos.left - LABOR_TOOLTIP_OFFSET : undefined)
  const tooltipDate = hover?.date ?? (peerDateIndex >= 0 ? dates[peerDateIndex] : '')
  const tooltipMinWidth = pctTooltip ? PCT_TOOLTIP_MIN_WIDTH : DEFAULT_TOOLTIP_MIN_WIDTH
  const tooltipWidth = tooltipSize?.width ?? tooltipMinWidth
  const tooltipHeight = tooltipSize?.height ?? TOOLTIP_HEIGHT_FALLBACK
  const containerWidth = chartRef.current?.clientWidth ?? width
  const containerHeight = chartRef.current?.clientHeight ?? height
  const { left: tooltipLeft, top: tooltipTop } = fitLaborTooltipInContainer({
    preferredLeft: preferredTooltipLeft,
    preferredTop: preferredTooltipTop,
    tooltipWidth,
    tooltipHeight,
    containerWidth,
    containerHeight,
    cursorX: tooltipCursorX,
    offsetX: LABOR_TOOLTIP_OFFSET,
  })

  return (
    <div ref={chartRef} className="relative flex min-h-[400px] flex-1 flex-col">
      {paneLabel ? (
        <p className="mb-2 shrink-0 text-xs font-medium text-gray-600">{paneLabel}</p>
      ) : null}
      <div ref={plotRef} className="relative min-h-0 w-full flex-1">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMinYMin meet"
          className="absolute inset-0 h-full w-full"
          role="img"
          aria-label={ariaLabel}
        >
          <g transform={`translate(${margin.left},${margin.top})`}>
            {yAxisTicks.map((value) => {
              const y = yScale(value)
              return (
                <g key={value}>
                  <line x1={0} y1={y} x2={innerWidth} y2={y} stroke="#e5e7eb" />
                  <text
                    x={-8}
                    y={y + 4}
                    textAnchor="end"
                    fill="#6b7280"
                    fontSize={axisFontSize}
                  >
                    {yAxisFormatter(value)}
                  </text>
                </g>
              )
            })}

            {[...visiblePaths]
              .sort((a, b) => {
                if (a.key === activeKey) return 1
                if (b.key === activeKey) return -1
                return 0
              })
              .map((line) => {
              const dimmed = activeKey != null && activeKey !== line.key
              const highlighted = activeKey === line.key
              const plotValues = line.values.map((point) => point.value)
              const { pathDs, isolatedPoints } = buildLaborLinePaths(plotValues, xStep, yScale)
              const strokeWidth = highlighted ? 2.5 : 1.5
              return (
                <g key={line.key}>
                  {pathDs.map((pathD, pathIndex) => (
                    <path
                      key={`${line.key}-path-${pathIndex}`}
                      d={pathD}
                      fill="none"
                      stroke={line.color}
                      strokeWidth={strokeWidth}
                      opacity={dimmed ? 0.18 : 1}
                    />
                  ))}
                  {isolatedPoints.map((point, pointIndex) => (
                    <circle
                      key={`${line.key}-iso-${pointIndex}`}
                      cx={point.x}
                      cy={point.y}
                      r={ISOLATED_POINT_RADIUS}
                      fill={line.color}
                      stroke={line.color}
                      strokeWidth={strokeWidth}
                      opacity={dimmed ? 0.18 : 1}
                    />
                  ))}
                </g>
              )
            })}

            {syncHoverYValue != null ? (
              <line
                x1={0}
                y1={yScale(syncHoverYValue)}
                x2={innerWidth}
                y2={yScale(syncHoverYValue)}
                stroke={TRACKING_LINE_COLOR}
                strokeWidth={1}
                strokeDasharray={LABOR_TRACKING_LINE_DASH}
                pointerEvents="none"
              />
            ) : null}

            {trackingDateIndex >= 0 ? (
              <line
                x1={trackingDateIndex * xStep}
                y1={0}
                x2={trackingDateIndex * xStep}
                y2={innerHeight}
                stroke={TRACKING_LINE_COLOR}
                strokeWidth={1}
                strokeDasharray={LABOR_TRACKING_LINE_DASH}
                pointerEvents="none"
              />
            ) : null}

            {dates.map((date, index) => (
              <rect
                key={date}
                x={index * xStep - Math.max(xStep / 2, 6)}
                y={0}
                width={Math.max(xStep, 12)}
                height={innerHeight}
                fill="transparent"
                className="cursor-pointer"
                onMouseEnter={(event) => updateHover(event, index)}
                onMouseMove={(event) => updateHover(event, index)}
                onMouseLeave={clearHover}
              />
            ))}

            <line x1={0} y1={innerHeight} x2={innerWidth} y2={innerHeight} stroke="#d1d5db" />
            {xLabelIndexes.map((index) => {
              const date = dates[index]
              if (!date) return null
              return (
                <text
                  key={date}
                  x={index * xStep}
                  y={innerHeight + 16}
                  textAnchor="middle"
                  fill="#6b7280"
                  fontSize={axisFontSize}
                >
                  {formatLaborDateLabel(date)}
                </text>
              )
            })}
          </g>
        </svg>
      </div>

      {showTooltip && tooltipDateIndex >= 0 ? (
        <div
          ref={tooltipRef}
          className={`pointer-events-none absolute z-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg ${
            pctTooltip ? 'min-w-[20rem]' : 'min-w-[12rem]'
          }`}
          style={{ left: tooltipLeft, top: tooltipTop }}
        >
          <p className="font-medium text-gray-900">{tooltipDate}</p>
          {pctTooltip ? (
            <div className="mt-1 grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto] items-center gap-x-2 gap-y-1">
              <span />
              <span />
              <span className="text-right text-[10px] font-medium text-gray-400">
                {pctTooltip.numeratorLabel}
              </span>
              <span className="text-right text-[10px] font-medium text-gray-400">
                {pctTooltip.denominatorLabel}
              </span>
              <span className="text-right text-[10px] font-medium text-gray-400">
                {pctTooltip.ratioLabel ?? 'Ratio'}
              </span>
              {visiblePaths.map((line) => {
                const point = line.values[tooltipDateIndex]
                const value = point?.value ?? null
                if (value === 0 && visiblePaths.length > 3) return null
                const dimmed = activeKey != null && activeKey !== line.key
                const numerator =
                  point?.numerator == null || point.numerator === ''
                    ? null
                    : parseLaborValue(point.numerator)
                const denominator =
                  point?.denominator == null || point.denominator === ''
                    ? null
                    : parseLaborValue(point.denominator)
                const cells = formatLaborPctTooltipCells(
                  { value, numerator, denominator },
                  pctTooltip,
                )
                return (
                  <div key={line.key} className="contents">
                    <span
                      className={`inline-block h-2 w-2 shrink-0 rounded-full ${dimmed ? 'opacity-40' : ''}`}
                      style={{ backgroundColor: line.color }}
                      aria-hidden
                    />
                    <span
                      className={`truncate ${dimmed ? 'text-gray-400 opacity-40' : 'font-medium text-gray-900'}`}
                      title={line.key}
                    >
                      {line.key}
                    </span>
                    <span className={`tabular-nums text-right ${dimmed ? 'text-gray-400 opacity-40' : 'text-gray-700'}`}>
                      {cells.numerator}
                    </span>
                    <span className={`tabular-nums text-right ${dimmed ? 'text-gray-400 opacity-40' : 'text-gray-700'}`}>
                      {cells.denominator}
                    </span>
                    <span className={`tabular-nums text-right ${dimmed ? 'text-gray-400 opacity-40' : 'font-medium text-gray-900'}`}>
                      {cells.ratio}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            visiblePaths.map((line) => {
              const value = line.values[tooltipDateIndex]?.value ?? null
              if (value === 0 && visiblePaths.length > 3) return null
              const dimmed = activeKey != null && activeKey !== line.key
              return (
                <p
                  key={line.key}
                  className={`mt-1 flex items-center gap-1.5 ${
                    dimmed ? 'text-gray-400 opacity-40' : 'font-medium text-gray-900'
                  }`}
                >
                  <span
                    className="inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: line.color }}
                    aria-hidden
                  />
                  <span>
                    {line.key}: {value == null ? '—' : valueFormatter(value)}
                  </span>
                </p>
              )
            })
          )}
        </div>
      ) : null}

      {showLegend ? (
        <div className="mt-3 flex shrink-0 flex-wrap gap-3 text-xs text-gray-600">
          {linePaths.map((line) => {
            const hidden = hiddenKeys.has(line.key)
            const dimmed = !hidden && activeKey != null && activeKey !== line.key
            return (
              <button
                key={line.key}
                type="button"
                aria-pressed={!hidden}
                title={hidden ? `Show ${line.key}` : `Hide ${line.key}`}
                className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 transition-opacity ${
                  hidden ? 'opacity-35' : dimmed ? 'opacity-40' : ''
                }`}
                onClick={() => {
                  setHiddenKeys(
                    nextHiddenSeriesKeys(
                      hiddenKeys,
                      line.key,
                      linePaths.map((item) => item.key),
                    ),
                  )
                }}
                onMouseEnter={() => {
                  if (!hidden) setActiveKey(line.key)
                }}
                onMouseLeave={() => setActiveKey(null)}
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ backgroundColor: line.color }}
                />
                <span
                  className={`max-w-[10rem] truncate ${hidden ? 'line-through' : ''}`}
                  title={line.key}
                >
                  {line.key}
                </span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
